import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer } from "lucide-react";

const printable = (s, t) => t.readiness && t.readiness.kind !== "incomplete" && t.readiness[s.kind];
const Opt = ({ on, onClick, testid, children }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`px-3 h-9 rounded-md border text-sm ${on ? "bg-black text-white border-black" : "bg-white hover:bg-zinc-50"}`}>{children}</button>
);

/** Stampa schede cartacee: A4 per protocollo o A3 batteria; precompilata con anagrafica o vuota. Nessun valore misurato stampato. */
export default function PrintPanel({ session: s, onPrinted }) {
  const [sel, setSel] = useState(() => s.tests.filter((t) => printable(s, t)).map((t) => t.id));
  const [layout, setLayout] = useState("a4");
  const [fill, setFill] = useState("names");
  const [rows, setRows] = useState(30);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/test-sessions/${s.id}/print.pdf`, { params: { layout, fill, rows, test_ids: sel.join(",") }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `${s.code}_${layout === "a4" ? "A4_schede" : "A3_batteria"}${fill === "blank" ? "_vuota" : ""}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF generato"); onPrinted?.();
    } catch (e) {
      const t = await e.response?.data?.text?.(); let msg = "Errore di stampa";
      try { msg = JSON.parse(t).detail || msg; } catch { /* risposta non JSON */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 space-y-4" data-testid="print-panel">
      <div className="text-xs text-zinc-500">Schede in bianco e nero con versione del protocollo della sessione. Si stampa solo l'anagrafica: tempi e valutazioni non vengono riportati né stimati.</div>
      <div className="space-y-1">
        <div className="text-sm font-medium">Test da stampare</div>
        {s.tests.map((t) => {
          const ok = printable(s, t);
          return (
            <label key={t.id} className={`flex items-center gap-2 text-sm ${ok ? "" : "text-zinc-400"}`} data-testid={`print-test-${t.id}`}>
              <input type="checkbox" disabled={!ok} checked={ok && sel.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`print-test-check-${t.id}`} />
              {t.name} <span className="font-mono text-[11px]">v{s.test_versions[t.id]} · {s.planned_attempts[t.id]} tent.{t.fencing_technical ? " · tecnico" : ""}</span>
              {!ok && <span className="text-[11px] text-red-700">{t.readiness?.label}</span>}
            </label>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-6">
        <div className="space-y-1"><div className="text-sm font-medium">Formato</div><div className="flex gap-1">
          <Opt testid="print-layout-a4" on={layout === "a4"} onClick={() => setLayout("a4")}>A4 · una scheda per test</Opt>
          <Opt testid="print-layout-a3" on={layout === "a3"} onClick={() => setLayout("a3")}>A3 orizzontale · batteria</Opt></div></div>
        <div className="space-y-1"><div className="text-sm font-medium">Contenuto</div><div className="flex gap-1">
          <Opt testid="print-fill-names" on={fill === "names"} onClick={() => setFill("names")}>Con atleti presenti</Opt>
          <Opt testid="print-fill-blank" on={fill === "blank"} onClick={() => setFill("blank")}>Griglia vuota</Opt></div></div>
        <div className="space-y-1"><div className="text-sm font-medium">Righe minime</div>
          <select data-testid="print-rows" value={rows} onChange={(e) => setRows(Number(e.target.value))} className="h-9 border border-zinc-300 rounded-md px-2 text-sm">
            {[25, 26, 27, 28, 29, 30].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></div>
      </div>
      {s.kind === "pilota" && <div className="text-xs text-amber-800" data-testid="print-pilot-note">Sessione Pilota: le schede riportano il contrassegno PILOTA.</div>}
      <Button data-testid="print-download-btn" disabled={busy || sel.length === 0} onClick={download}><Printer className="w-4 h-4 mr-1" />{busy ? "Generazione…" : "Scarica PDF"}</Button>
    </div>
  );
}
